import { ICertificate } from '../../models/certificate';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { BasicService } from '../../providers/basic';
import { environment as config } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { AuthHttp } from 'angular2-jwt';
import { AuthService } from '../auth/auth.service';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/observable/of';
import { ISession } from '../../models/session';

@Injectable()
export class CertificatesService extends BasicService {
  private API = config.certificates.url;

  constructor(
    protected authService: AuthService,
    protected translate: TranslateService,
    private http: AuthHttp
  ) {
    super(authService, translate);
  }

  private getDefaultCertificate(session: ISession): ICertificate {
    return {
      session_id: +session.id,
      training_title: '',
      organization: '',
      event_date: new Date().toISOString(),
      location: '',
      max_certified_duration_minutes: 0,
      min_connection_minutes_for_eligibility: 0,
      president_full_name: '',
      president_title: '',
      event_page_url: '',
      questionnaire_url: ''
    };
  }

  private fixUrl(url: string) {
    return url.startsWith('//') ? `https://${url}` : url;
  }

  public get(session: ISession, notify: boolean = false): Observable<ICertificate> {
    return this.http
      .get(`${this.API}/certificates?sessionId=${session.id}`)
      .map(res => {
        const data = this.extractData(res, notify);
        if (Array.isArray(data) && data.length === 0) {
          return this.getDefaultCertificate(session);
        }
        if (Array.isArray(data) && data.length > 0) {
          return data[0];
        }
        return data;
      })
      .catch(err => {
        this.handleError(err);
        return Observable.of(this.getDefaultCertificate(session));
      });
  }

  public update(id: number, certificate: ICertificate, notify: boolean = true): Observable<ICertificate> {
    return this.http
      .patch(`${this.API}/certificates/${id}`, certificate)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleError(err));
  }

  public create(certificate: ICertificate, notify: boolean = true): Observable<ICertificate> {
    return this.http
      .post(`${this.API}/certificates`, certificate)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleError(err));
  }

  public sendEmail(session: ISession, userIds: number[], notify: boolean = true): Observable<any> {
    const userIdsParam = userIds.join(',');
    return this.http
      .post(`${this.API}/email/send/${session.id}?userIds=${userIdsParam}&lang=${session.language}`, {})
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleError(err));
  }

  public generateCertificate(session: ISession, userId: number, notify: boolean = true): Observable<any> {
    return this.http
      .post(`${this.API}/pdf/generate?sessionId=${session.id}&userId=${userId}&lang=${session.language}`, {})
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleError(err));
  }

  public downloadCertificate(session: ISession, userId: number): Observable<{blob: Blob, filename: string}> {
    return this.http
      .get(`${this.API}/pdf/download?sessionId=${session.id}&userId=${userId}&lang=${session.language}&_t=${+new Date()}`, {
        responseType: 3 // ResponseContentType.Blob
      })
      .map(res => {
        const contentDisposition = res.headers.get('content-disposition');
        let filename = `certificate_${session.id}_${userId}.pdf`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=([^;]+)/);
          if (match && match[1]) {
            filename = match[1].replace(/"/g, '').trim();
          }
        }
        return { blob: res.blob(), filename };
      })
      .catch(err => this.handleError(err));
  }

  public previewCertificate(session: ISession): Observable<Blob> {
    return this.http
      .get(`${this.API}/pdf/preview?sessionId=${session.id}&lang=${session.language}`, {
        responseType: 3 // ResponseContentType.Blob
      })
      .map(res => res.blob())
      .catch(err => this.handleError(err));
  }

  public getTags(session: ISession, notify: boolean = true) {
    return this.http
      .get(`${this.API}/certificates/tags/comletions?sessionId=${session.id}`)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleError(err));
  }

  public getCertificateLogoUrl(session: ISession) {
    return this.fixUrl(`${this.API}/certificate-image/${session.id}/header_logo_image`);
  }

  public getCertificateStampUrl(session: ISession) {
    return this.fixUrl(`${this.API}/certificate-image/${session.id}/stamp_image`);
  }

  public getCertificateSignatureUrl(session: ISession) {
    return this.fixUrl(`${this.API}/certificate-image/${session.id}/signature_image`);
  }

  public copyCertificate(fromSession: ISession, toSessionId: number): Observable<ICertificate | null> {
    return this.get(fromSession).mergeMap(certificate => {
      if (!certificate || !certificate.id) {
        return Observable.of(null);
      }
      const copy: ICertificate = {
        session_id: toSessionId,
        training_title: certificate.training_title,
        organization: certificate.organization,
        event_date: certificate.event_date,
        location: certificate.location,
        max_certified_duration_minutes: certificate.max_certified_duration_minutes,
        min_connection_minutes_for_eligibility: certificate.min_connection_minutes_for_eligibility,
        header_logo_image: certificate.header_logo_image,
        stamp_image: certificate.stamp_image,
        signature_image: certificate.signature_image,
        president_full_name: certificate.president_full_name,
        president_title: certificate.president_title,
        event_page_url: certificate.event_page_url,
        questionnaire_url: certificate.questionnaire_url,
        pdf_template: certificate.pdf_template,
        participant_fields: certificate.participant_fields,
      };
      return this.create(copy, false);
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/certificates.service.ts